// Global operation lock (docs/design/PORTFOLIO_CENTRIC_UI.md). While ONE book-changing op is in flight
// (record fill / place / trim / close / cover) AND its follow-up refresh settles, a "Processing…" veil
// covers the content and blocks every op control — so the user can't double-fire on a row whose position
// is still adjusting in real time (the reported jitter). One lock, app-wide (Portfolio + Today + Trading).
//
// The veil lifts only when ALL of: the op RESOLVED (armed) · a minimum FLOOR elapsed (so "Processing…"
// is always visible, never a flash) · the page's post-op refresh SETTLED (loading false — pages push
// their `port.loading` via setLoading). A hard CAP force-lifts it so a stalled refresh can never hang the
// UI. When a page doesn't track loading (loading stays false), it degrades to floor-only. [M3]
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const OpCtx = createContext({ busy: false, begin: () => {}, settle: () => {}, setLoading: () => {} });

export function OperationProvider({ children }) {
  const [busy, setBusy] = useState(false);
  const s = useRef({ armed: false, floorDone: false, loading: false, floor: null, cap: null });

  const clear = () => { const r = s.current; if (r.floor) clearTimeout(r.floor); if (r.cap) clearTimeout(r.cap); r.floor = r.cap = null; };
  const evaluate = useCallback(() => {
    const r = s.current;
    if (r.armed && r.floorDone && !r.loading) { clear(); setBusy(false); }
  }, []);
  const begin = useCallback(() => {
    const r = s.current; clear(); r.armed = false; r.floorDone = false; setBusy(true);
  }, []);
  const settle = useCallback((floorMs = 900, capMs = 6000) => {
    const r = s.current; r.armed = true;
    if (r.floor) clearTimeout(r.floor);
    r.floor = setTimeout(() => { r.floorDone = true; r.floor = null; evaluate(); }, floorMs);
    if (r.cap) clearTimeout(r.cap);
    r.cap = setTimeout(() => { clear(); setBusy(false); }, capMs);   // safety: never hang if a refresh stalls
  }, [evaluate]);
  // Pages push their post-op data-load state (port.loading) so the veil lifts when the position actually
  // settles, not on a fixed timer. ponytail: floor+cap bound both ends; the exact refresh is the middle.
  const setLoading = useCallback((v) => { s.current.loading = !!v; evaluate(); }, [evaluate]);
  useEffect(() => clear, []);

  return (
    <OpCtx.Provider value={{ busy, begin, settle, setLoading }}>
      {children}
      {busy && (
        <div className="proc-veil" role="status" aria-live="polite" aria-busy="true">
          <span className="proc-veil__spin" aria-hidden="true" /> Processing…
        </div>
      )}
    </OpCtx.Provider>
  );
}

export const useOperation = () => useContext(OpCtx);
